import {useContext} from 'react';

import {ErrorMessage, Field} from 'formik';
import {useTranslation} from 'react-i18next';

import {Box, Grid, Slider, SxProps, Typography, useTheme, useMediaQuery} from '@mui/material';

import {SelectFieldQuestionnaire} from '../forms/demographics-form-fields/SelectFieldQuestionnaire';

import {EmpionPaper} from '../../../../../../../components/EmpionPaper/EmpionPaper';
import {LanguageContext} from '../../../../../../../contexts/LanguageContext/LanguageContext';
import {greenColors, orangeColors} from '../../../../../../../layouts/theme/designSystem';
import {
  SUPPORTED_AGE_GROUPS,
  SUPPORTED_GENDER_IDENTITIES,
  SUPPORTED_SPOKEN_LANGUAGES,
  SUPPORTED_WORK_MODES,
} from '../../../../../../../lib/constants';
import {CompanySurveyQuestionnaire} from '../../../types/SurveyQuestionnaireTypes';

interface DemographicQuestionsQuestionsProps {
  formik: any;
  demographicQuestions: CompanySurveyQuestionnaire['surveyDemographic'] | undefined;
}

export const DemographicQuestionsQuestions = ({formik, demographicQuestions}: DemographicQuestionsQuestionsProps) => {
  const {t} = useTranslation();

  const language = useContext(LanguageContext);

  const theme = useTheme();
  const isXsScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const handleBackgroundColor = () => {
    return orangeColors[4];
  };

  const paperStyles: SxProps = {
    backgroundColor: 'white',
    borderRadius: '16px',
    width: {xs: '100%', sm: '100%', md: '736px'},
    padding: {xs: '14px', sm: '18px', md: '28px'},
    marginTop: {xs: '1rem', md: '1.5rem'},
    '&:first-of-type': {
      marginTop: {xs: '1.5rem', md: '2rem'},
    },
    display: {sm: 'flex'},
    flexDirection: {sm: 'column'},
    alignItems: {sm: 'center'},
    justifyContent: {sm: 'center'},
  };

  const ageGroupOptions = SUPPORTED_AGE_GROUPS.map((ageGroup) => ({
    id: ageGroup.id,
    label: ageGroup.label[language as 'en' | 'de'],
  }));

  const genderOptions = SUPPORTED_GENDER_IDENTITIES.map((gender) => ({
    id: gender.id,
    label: t(`shared.genders.${gender.id}`),
  }));

  const handleDepartmentsAndLocations = (options: any) => {
    const listOfOptions = options.map((option: any) => {
      return {
        id: option.id,
        label: language === 'en' ? option.name.en : option.name.de,
      };
    });

    return listOfOptions;
  };

  const workModeOptions = SUPPORTED_WORK_MODES.map((mode) => ({
    id: mode.id,
    label: t(`shared.workModes.${mode.id}`),
  }));

  const languageOptions = SUPPORTED_SPOKEN_LANGUAGES.map((language) => ({
    id: language.id,
    label: t(`shared.spokenLanguages.${language.id}`),
  }));

  return (
    <Grid item xs={12}>
      {demographicQuestions?.questions?.map((question) => {
        switch (question.id) {
          case 'ageGroup':
            return (
              <EmpionPaper sx={paperStyles} key={question.id}>
                <SelectFieldQuestionnaire
                  label={t('companySurveys.public.demographic.formFields.ageGroupLabel')}
                  options={ageGroupOptions}
                  name={'ageGroup'}
                />
              </EmpionPaper>
            );
          case 'gender':
            return (
              <EmpionPaper sx={paperStyles} key={question.id}>
                <SelectFieldQuestionnaire
                  label={t('companySurveys.public.demographic.formFields.genderLabel')}
                  options={genderOptions}
                  name={'gender'}
                />
              </EmpionPaper>
            );
          case 'companyDepartment':
            return (
              <EmpionPaper sx={paperStyles} key={question.id}>
                <SelectFieldQuestionnaire
                  label={t('companySurveys.public.demographic.formFields.departmentLabel')}
                  options={handleDepartmentsAndLocations(question.options)}
                  name={'companyDepartment'}
                />
              </EmpionPaper>
            );
          case 'companyLocation':
            return (
              <EmpionPaper sx={paperStyles} key={question.id}>
                <SelectFieldQuestionnaire
                  label={t('companySurveys.public.demographic.formFields.locationLabel')}
                  options={handleDepartmentsAndLocations(question.options)}
                  name={'companyLocation'}
                />
              </EmpionPaper>
            );

          case 'workMode':
            return (
              <EmpionPaper sx={paperStyles} key={question.id}>
                <SelectFieldQuestionnaire
                  label={t('companySurveys.public.demographic.formFields.workModeLabel')}
                  options={workModeOptions}
                  name={'workMode'}
                />
              </EmpionPaper>
            );
          case 'spokenLanguage':
            return (
              <EmpionPaper sx={paperStyles} key={question.id}>
                <SelectFieldQuestionnaire
                  label={t('companySurveys.public.demographic.formFields.languageLabel')}
                  options={languageOptions}
                  name={'spokenLanguage'}
                />
              </EmpionPaper>
            );

          case 'genderDiversityInLeadership':
            return (
              <EmpionPaper sx={paperStyles} key={question.id}>
                <Box
                  sx={{
                    width: '100%',
                  }}
                >
                  <Typography variant='h4' color={greenColors[700]} gutterBottom>
                    {t('companySurveys.public.demographic.formFields.genderDiversityLabel')}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: {xs: 'space-between', sm: 'center'},
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant='body1'
                      color={greenColors[700]}
                      sx={{
                        mb: {xs: 2, sm: 8},
                      }}
                    >
                      {t('companySurveys.public.demographic.formFields.lowerEnd')}
                    </Typography>

                    <Field
                      component={Slider}
                      name={'genderDiversityInLeadership'}
                      defaultValue={formik.values.genderDiversityInLeadership}
                      value={formik.values.genderDiversityInLeadership}
                      min={1}
                      max={6}
                      step={1}
                      style={{
                        color: handleBackgroundColor(),
                        height: '15px',
                        width: '60%',
                        display: isXsScreen ? 'none' : 'block',
                      }}
                      valueLabelDisplay='auto'
                      onChange={(e: any) => {
                        formik.setFieldValue('genderDiversityInLeadership', e.target.value);
                      }}
                    />

                    <Typography
                      variant='body1'
                      color={greenColors[700]}
                      sx={{
                        mb: {xs: 2, sm: 8},
                      }}
                    >
                      {t('companySurveys.public.demographic.formFields.higherEnd')}
                    </Typography>
                  </Box>

                  <Field
                    component={Slider}
                    name={'genderDiversityInLeadership'}
                    defaultValue={formik.values.genderDiversityInLeadership}
                    value={formik.values.genderDiversityInLeadership}
                    min={1}
                    max={6}
                    step={1}
                    style={{
                      color: handleBackgroundColor(),
                      height: '15px',
                      width: '10%',
                      display: isXsScreen ? 'block' : 'none',
                    }}
                    valueLabelDisplay='auto'
                    onChange={(e: any) => {
                      formik.setFieldValue('genderDiversityInLeadership', e.target.value);
                    }}
                  />
                  <Typography variant='body2' color='error'>
                    <ErrorMessage name={'genderDiversityInLeadership'} component='span' />
                  </Typography>
                </Box>
              </EmpionPaper>
            );
        }
      })}
    </Grid>
  );
};
